import React, { useState } from "react"
import { VStack, Text } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql/graphql"

import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import {
  ActivityAttributesConfigurable,
  AttributeValues,
  ActivityAttributesConfigurableRef,
} from "./ActivityAttributesConfigurable"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { formatStatus } from "@hydrologiq/shared/utils/activities/status"
import { useActivityMutations } from "@hydrologiq/shared/hooks/activities"

interface ActivityAttributeEditorForStatusProps {
  order?: gqlTypes.Order
  asset?: gqlTypes.Asset
  activities: gqlTypes.Activity[]
  activity: gqlTypes.Activity
  eventKind: gqlTypes.ActivityEventKind
  existingValues: AttributeValues
  isStatusProgression?: boolean // True if changing status, false if editing existing
  onSuccess: () => void | Promise<void>
  onCancel: () => void
  onBack?: () => void
  onNavigateToBatchCreation?: () => void
  newlyCreatedBatch?: { id: string; name: string } | null
}

/**
 * Edits attributes for a specific status/event kind
 * Always shows all attribute fields - no dynamic add/remove
 * Can be used for:
 * 1. Progressing to a new status (with createActivityEvent)
 * 2. Editing attributes for an existing status (with create/update/delete attribute mutations)
 */
const ActivityAttributeEditorForStatus: React.FC<ActivityAttributeEditorForStatusProps> = ({
  order,
  asset,
  activities,
  activity,
  eventKind,
  existingValues,
  isStatusProgression = false,
  onSuccess,
  onCancel,
  onBack,
  onNavigateToBatchCreation,
  newlyCreatedBatch,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [values, setValues] = useState<AttributeValues>(existingValues)

  // Add ref for validation
  const attributesRef = React.useRef<ActivityAttributesConfigurableRef>(null)

  const { updateActivityAttributes, createActivityEvent } = useActivityMutations({
    refetchQueries: ["GetSuggestedFulfilments", "GetOrders", "AssetAvailabilityCalendar"],
    awaitRefetchQueries: false,
  })

  const handleStatusProgression = async (): Promise<boolean> => {
    let toastId: string | undefined
    try {
      setError(null)
      toastId = showLoadingToast(`Marking activity as ${formatStatus(eventKind)}...`)

      // Get asset ID from values, or fallback to first order asset or activity asset
      // Asset value might be an ID or name, so we need to resolve it
      const assetIdOrName = values.asset
      let assetId: string | undefined = undefined

      if (order) {
        if (assetIdOrName) {
          // Check if it's already an ID by matching against order assets or activity assets
          const orderAssets = order?.assets || []
          const activityAssets = activity.assets || []
          const allAssets = [...orderAssets, ...activityAssets]
          const matchingAsset = allAssets.find((a) => a.id === assetIdOrName || a.name === assetIdOrName)
          assetId = matchingAsset?.id
        }

        // Fallback to first activity asset if no asset selected
        if (!assetId && activity.assets && activity.assets.length > 0) {
          assetId = activity.assets[0].id
        }

        // Fallback to first order asset if no activity asset
        if (!assetId && order?.assets && order.assets.length > 0) {
          assetId = order.assets[0].id
        }
      } else if (asset) {
        assetId = asset.id
      }
      if (!assetId && activity.assets && activity.assets.length > 0) {
        assetId = activity.assets[0].id
      }

      if (!assetId) {
        throw new Error("No asset found for activity")
      }

      // Build attribute inputs from values
      const attributeInputs: gqlTypes.ActivityAttributeInput[] = []

      if (values.pressure) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.Pressure,
          value: values.pressure,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      if (values.quantity) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.Quantity,
          value: values.quantity,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      if (values.temperature) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.Temperature,
          value: values.temperature,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      // Add fill_level if present
      if (values.fill_level) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.FillLevel,
          value: values.fill_level,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      if (values.site) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.Site,
          value: values.site,
          site: values.site,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      if (values.batch) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.Batch,
          value: values.batch,
          batch: values.batch,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      if (values.note) {
        attributeInputs.push({
          attribute: gqlTypes.ActivityAttributeKind.Note,
          value: values.note,
          event_kind: eventKind,
          asset: assetId,
        })
      }

      await createActivityEvent({
        variables: {
          activity: activity.id,
          kind: eventKind,
          attributes: attributeInputs,
          ...(order ? { order: order.id } : {}),
        },
      })

      dismissToast(toastId)
      showSuccessToast(`Activity marked as ${formatStatus(eventKind)} successfully`)

      await onSuccess()
      return true
    } catch (error: any) {
      console.error("Error updating activity status:", error)
      if (toastId) {
        dismissToast(toastId)
      }
      setError(error?.message || "Failed to update activity status")
      showErrorToast("Failed to update activity status")
      return false
    }
  }

  const handleAttributeUpdate = async (): Promise<boolean> => {
    let toastId: string | undefined
    try {
      setError(null)
      toastId = showLoadingToast("Saving changes...")

      // Get asset ID from values, or fallback to first order asset or activity asset
      // Asset value might be an ID or name, so we need to resolve it
      const assetIdOrName = values.asset
      let assetId: string | undefined = undefined

      if (assetIdOrName) {
        // Check if it's already an ID by matching against order assets or activity assets
        const orderAssets = order?.assets || []
        const activityAssets = activity.assets || []
        const allAssets = [...orderAssets, ...activityAssets]
        const matchingAsset = allAssets.find((a) => a.id === assetIdOrName || a.name === assetIdOrName)
        assetId = matchingAsset?.id
      }

      // Fallback to first activity asset if no asset selected
      if (!assetId && activity.assets && activity.assets.length > 0) {
        assetId = activity.assets[0].id
      }

      // Fallback to first order asset if no activity asset
      if (!assetId && order?.assets && order.assets.length > 0) {
        assetId = order.assets[0].id
      }

      if (!assetId) {
        throw new Error("No asset found for activity")
      }

      // Get existing attributes for this event kind
      const existingAttrs = activity.attributes?.filter((attr) => attr.event_kind === eventKind) || []

      // Helper to find existing attribute by kind
      const findExisting = (kind: gqlTypes.ActivityAttributeKind) => {
        return existingAttrs.find((a) => a.attribute === kind)
      }

      // Build batch operations
      const creates: gqlTypes.ActivityAttributeInput[] = []
      const updates: gqlTypes.ActivityAttributeInput[] = []
      const deletes: string[] = []

      // Helper to handle attribute create/update/delete
      const handleAttributeChange = (
        kind: gqlTypes.ActivityAttributeKind,
        newValue: string | null,
        existing: any,
        extraVars?: { site?: string; batch?: string }
      ) => {
        // Check if there's an actual change to make
        const valueChanged = existing && existing.value !== newValue
        const siteChanged = extraVars?.site !== undefined && existing?.site?.id !== extraVars.site
        const batchChanged = extraVars?.batch !== undefined && existing?.batch?.id !== extraVars.batch
        const hasChanges = valueChanged || siteChanged || batchChanged

        if (newValue && !existing) {
          // Create new attribute
          creates.push({
            event_kind: eventKind,
            attribute: kind,
            value: newValue,
            asset: assetId,
            ...extraVars,
          })
        } else if (!newValue && existing) {
          // Delete existing attribute
          deletes.push(existing.id)
        } else if (newValue && existing && hasChanges) {
          // Update existing attribute (only if something actually changed)
          updates.push({
            id: existing.id,
            event_kind: eventKind,
            attribute: kind,
            value: newValue,
            asset: assetId,
            ...extraVars,
          })
        }
        // If newValue && existing && !hasChanges: do nothing (no change needed)
      }

      // Handle all simple value attributes
      handleAttributeChange(
        gqlTypes.ActivityAttributeKind.Pressure,
        values.pressure,
        findExisting(gqlTypes.ActivityAttributeKind.Pressure)
      )

      handleAttributeChange(
        gqlTypes.ActivityAttributeKind.Note,
        values.note,
        findExisting(gqlTypes.ActivityAttributeKind.Note)
      )

      handleAttributeChange(
        gqlTypes.ActivityAttributeKind.Quantity,
        values.quantity,
        findExisting(gqlTypes.ActivityAttributeKind.Quantity)
      )

      handleAttributeChange(
        gqlTypes.ActivityAttributeKind.Temperature,
        values.temperature,
        findExisting(gqlTypes.ActivityAttributeKind.Temperature)
      )

      // Tank level
      handleAttributeChange(
        gqlTypes.ActivityAttributeKind.FillLevel,
        values.fill_level,
        findExisting(gqlTypes.ActivityAttributeKind.FillLevel)
      )

      // Site (with extra site field)
      const existingSite = findExisting(gqlTypes.ActivityAttributeKind.Site)
      handleAttributeChange(gqlTypes.ActivityAttributeKind.Site, values.site, existingSite, {
        site: values.site || undefined,
      })

      const existingSource = findExisting(gqlTypes.ActivityAttributeKind.Source)
      handleAttributeChange(gqlTypes.ActivityAttributeKind.Source, values.source, existingSource, {
        site: values.source || undefined,
      })

      const existingDestination = findExisting(gqlTypes.ActivityAttributeKind.Destination)
      handleAttributeChange(gqlTypes.ActivityAttributeKind.Destination, values.destination, existingDestination, {
        site: values.destination || undefined,
      })

      // Batch (with extra batch field)
      const existingBatch = findExisting(gqlTypes.ActivityAttributeKind.Batch)
      handleAttributeChange(gqlTypes.ActivityAttributeKind.Batch, values.batch, existingBatch, {
        batch: values.batch || undefined,
      })

      // If there are no changes, skip the API call
      if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
        dismissToast(toastId)
        showSuccessToast("No changes to save")
        onBack?.() || onCancel()
        return true
      }

      // Execute single batch mutation (atomic operation)
      await updateActivityAttributes({
        variables: {
          activity: activity.id,
          creates: creates.length > 0 ? creates : undefined,
          updates: updates.length > 0 ? updates : undefined,
          deletes: deletes.length > 0 ? deletes : undefined,
          ...(order ? { order: order.id } : {}),
        },
      })

      dismissToast(toastId)
      showSuccessToast("Attributes updated successfully")

      // onSuccess will trigger a single refetch, avoiding multiple re-renders
      await onSuccess()
      return true
    } catch (error: any) {
      console.error("Error updating attributes:", error)
      if (toastId) {
        dismissToast(toastId)
      }
      setError(error?.message || "Failed to update attributes")
      showErrorToast("Failed to update attributes")
      return false
    }
  }

  const handleSubmit = async (_formData?: FormData): Promise<boolean> => {
    // Validate all fields before submitting
    if (attributesRef.current) {
      const isValid = attributesRef.current.validateAll()
      if (!isValid) {
        setError("Please fix validation errors before submitting")
        showErrorToast("Please fix validation errors")
        return false
      }
    }

    if (isStatusProgression) {
      return await handleStatusProgression()
    } else {
      return await handleAttributeUpdate()
    }
  }

  const contextText = isStatusProgression
    ? `Update attributes to provide additional context for status change: ${formatStatus(eventKind)}`
    : `Update attributes to provide additional context for this status: ${formatStatus(eventKind)}`

  return (
    <Form actionType="confirm" onSubmit={handleSubmit}>
      <VStack gap={4} align="stretch">
        {error && <ErrorAlert title="Error" message={error} />}

        <Text fontSize="sm" color="fg.muted">
          {contextText}
        </Text>

        <ActivityAttributesConfigurable
          ref={attributesRef}
          order={order}
          asset={asset}
          activities={activities}
          availableAssets={activity.assets}
          activity={activity}
          eventKind={eventKind}
          values={values}
          onChange={(newValues) => {
            setValues(newValues)
          }}
          onNavigateToBatchCreation={onNavigateToBatchCreation}
          newlyCreatedBatch={newlyCreatedBatch}
        />
      </VStack>
    </Form>
  )
}

export default React.memo(ActivityAttributeEditorForStatus)
